import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Plus, Search, Trash2, Edit2, X, FileDown, Mail, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["CONFIRMED", "EXCHANGE", "RETURN", "CANCELLED"];
const STATUS_META = {
  CONFIRMED: { label: "Confirmed", bg: "bg-[#1A4DFF]", text: "text-white", row: "bg-[#E7EEFF]", border: "border-[#1A4DFF]" },
  EXCHANGE:  { label: "Exchange",  bg: "bg-[#FFB800]", text: "text-black", row: "bg-[#FFF6D6]", border: "border-[#FFB800]" },
  RETURN:    { label: "Return",    bg: "bg-[#FF3333]", text: "text-white", row: "bg-[#FFE0E0]", border: "border-[#FF3333]" },
  CANCELLED: { label: "Cancelled by Customer", bg: "bg-[#6B7280]", text: "text-white", row: "bg-[#F1F2F4]", border: "border-[#6B7280]" },
};
const SIZES = ["XS", "S", "M", "L", "XL", "XXL"];

const todayIST = () => {
  const d = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  return d.toISOString().slice(0, 10);
};

export default function Orders() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [tab, setTab] = useState("ALL"); // ALL | CONFIRMED | EXCHANGE | RETURN
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState(""); // YYYY-MM-DD; empty = all dates
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [emailing, setEmailing] = useState(false);

  async function load() {
    const params = {};
    if (tab !== "ALL") params.status = tab;
    if (search) params.search = search;
    if (dateFilter) { params.date_from = dateFilter; params.date_to = dateFilter; }
    try {
      const [a, b] = await Promise.all([
        api.get("/orders", { params }),
        api.get("/orders/stats"),
      ]);
      setItems(a.data);
      setStats(b.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }
  // eslint-disable-next-line
  useEffect(() => { load(); }, [tab, search, dateFilter]);

  function onAdd() { setEditing(null); setOpen(true); }
  function onEdit(o) { setEditing(o); setOpen(true); }
  async function onDelete(o) {
    // o may be a single row OR a group ({key, rows, order_no})
    if (o && Array.isArray(o.rows)) {
      const count = o.rows.length;
      if (!window.confirm(`Delete entire order ${o.order_no} (${count} line${count > 1 ? "s" : ""})?`)) return;
      try {
        await Promise.all(o.rows.map((r) => api.delete(`/orders/${r.id}`)));
        toast.success(`Deleted ${count} line${count > 1 ? "s" : ""} of order ${o.order_no}`);
        load();
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
      return;
    }
    if (!window.confirm(`Delete this order line?`)) return;
    try { await api.delete(`/orders/${o.id}`); toast.success("Line deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }

  const [expanded, setExpanded] = useState({});
  const toggleExpanded = (key) => setExpanded((m) => ({ ...m, [key]: !m[key] }));
  const groupedOrders = useMemo(() => {
    const map = new Map();
    for (const o of items) {
      const key = o.group_id || `${o.order_no}::${o.order_date}::${o.status}::${o.payment_mode}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          rows: [],
          order_no: o.order_no,
          order_date: o.order_date,
          status: o.status,
          payment_mode: o.payment_mode,
        });
      }
      map.get(key).rows.push(o);
    }
    return Array.from(map.values()).map((g) => ({
      ...g,
      totalQty: g.rows.reduce((a, r) => a + (Number(r.quantity) || 0), 0),
      totalAmount: g.rows.reduce((a, r) => a + (Number(r.price) || 0) * (Number(r.quantity) || 0), 0),
    }));
  }, [items]);

  async function emailReport() {
    setEmailing(true);
    try {
      const payload = { kind: "orders" };
      if (tab !== "ALL") payload.status = tab;
      if (dateFilter) { payload.date_from = dateFilter; payload.date_to = dateFilter; }
      const { data } = await api.post("/reports/email", payload);
      toast.success(`Report emailed to ${(data.recipients || []).join(", ") || "recipients"} · ${data.rows} rows`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setEmailing(false); }
  }


  async function downloadExcel(forDate) {
    try {
      // forDate: explicit date string overrides dateFilter; null = use current dateFilter
      const targetDate = forDate ?? dateFilter;
      const params = {};
      if (tab !== "ALL") params.status = tab;
      if (targetDate) { params.date_from = targetDate; params.date_to = targetDate; }
      const res = await api.get("/exports/orders.xlsx", { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const suffix = tab !== "ALL" ? `-${tab.toLowerCase()}` : "";
      const dateSuffix = targetDate ? `-${targetDate}` : `-${new Date().toISOString().slice(0, 10)}`;
      a.href = url;
      a.download = `closet-orders${suffix}${dateSuffix}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(targetDate ? `Excel for ${targetDate} ready.` : "Orders exported.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  return (
    <div className="p-4 sm:p-6 lg:p-10" data-testid="orders-page">
      <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
        <div>
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-2">/ LEDGER · DAILY ORDERS</div>
          <h1 className="section-title text-5xl lg:text-6xl">Orders.</h1>
          <p className="text-zinc-600 mt-3 text-sm max-w-xl">Log every order made and dispatched. Track exchanges and returns in one place.</p>
        </div>
        <div className="flex flex-col gap-2 self-start">
          <button onClick={onAdd} className="brut-btn px-5 py-3 inline-flex items-center gap-2" data-testid="add-order-btn">
            <Plus size={16} /> New Order
          </button>
          <button onClick={() => downloadExcel(null)} className="brut-btn-outline px-5 py-2.5 inline-flex items-center gap-2" data-testid="download-orders-excel-btn">
            <FileDown size={16} /> Download Excel {dateFilter ? `· ${dateFilter}` : "· all"}
          </button>
          <button onClick={() => downloadExcel(todayIST())} className="brut-btn-outline px-5 py-2.5 inline-flex items-center gap-2" data-testid="download-orders-today-btn">
            <FileDown size={16} /> Today&apos;s Excel ({todayIST()})
          </button>
          <button onClick={emailReport} disabled={emailing} className="brut-btn-outline px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-60" data-testid="email-orders-report-btn">
            <Mail size={16} /> {emailing ? "Sending…" : "Email Report"}
          </button>
        </div>
      </header>

      {/* Date filter strip */}
      <div className="brut-card p-4 mb-5 flex flex-wrap items-center gap-3" data-testid="date-filter-strip">
        <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">Filter by date</div>
        <input
          type="date"
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          className="brut-input font-mono-ds max-w-[180px]"
          data-testid="orders-date-filter"
        />
        <button
          onClick={() => setDateFilter(todayIST())}
          className={`px-3 py-2 border-2 border-black font-mono-ds text-[11px] uppercase tracking-wider transition-all ${
            dateFilter === todayIST() ? "bg-black text-white shadow-[3px_3px_0_0_#0a0a0a]" : "bg-white hover:bg-zinc-100"
          }`}
          data-testid="filter-today-btn"
        >Today</button>
        <button
          onClick={() => setDateFilter("")}
          className={`px-3 py-2 border-2 border-black font-mono-ds text-[11px] uppercase tracking-wider transition-all ${
            dateFilter === "" ? "bg-black text-white shadow-[3px_3px_0_0_#0a0a0a]" : "bg-white hover:bg-zinc-100"
          }`}
          data-testid="filter-clear-date-btn"
        >All Dates</button>
        {dateFilter && (
          <button
            onClick={() => downloadExcel(dateFilter)}
            className="ml-auto brut-btn px-4 py-2 inline-flex items-center gap-2"
            data-testid="download-orders-date-btn"
          >
            <FileDown size={14} /> Download {dateFilter} Excel
          </button>
        )}
      </div>

      {/* Status tabs */}
      <div className="flex flex-wrap gap-2 mb-5">
        <Tab id="ALL" active={tab} setTab={setTab} label="All" count={Object.values(stats).reduce((a, b) => a + (b.count || 0), 0)} />
        {STATUSES.map((s) => (
          <Tab key={s} id={s} active={tab} setTab={setTab} label={STATUS_META[s].label} count={stats[s]?.count || 0} status={s} />
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-5 max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          data-testid="orders-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by order #, product, SKU, customer name or phone…"
          className="brut-input pl-10"
        />
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Legend status="CONFIRMED" />
        <Legend status="EXCHANGE" />
        <Legend status="RETURN" />
        <Legend status="CANCELLED" />
      </div>

      {/* Table — grouped by order # / group_id so one order = one row with items summary */}
      <div className="brut-card overflow-x-auto">
        <table className="brut-table min-w-[1200px]">
          <thead>
            <tr>
              <th className="w-8"></th>
              <th>Date</th><th>Order #</th>
              <th className="min-w-[180px]">Customer</th>
              <th className="min-w-[240px]">Items</th>
              <th>Qty</th>
              <th className="min-w-[110px]">Sizes</th>
              <th className="min-w-[110px]">Colors</th>
              <th>Payment</th><th>Status</th><th>Value (₹)</th>
              <th className="w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            {groupedOrders.map((g) => (
              <OrderGroupRow key={g.key} group={g} onEdit={onEdit} onDelete={onDelete}
                expanded={expanded[g.key]} onToggle={() => toggleExpanded(g.key)} />
            ))}
            {groupedOrders.length === 0 && <tr><td colSpan={12} className="text-center py-10 text-zinc-500 font-mono-ds text-xs uppercase">No orders yet · Click &quot;New Order&quot; to add the first one</td></tr>}
          </tbody>
          {groupedOrders.length > 0 && (
            <tfoot>
              <tr className="bg-black text-white font-display font-black" data-testid="orders-totals-row">
                <td colSpan={5} className="text-right pr-4 font-mono-ds text-[11px] tracking-widest uppercase">Totals →</td>
                <td className="font-mono-ds font-black text-base" data-testid="orders-totals-qty">
                  {groupedOrders.reduce((a, g) => a + g.totalQty, 0)}
                </td>
                <td colSpan={4}></td>
                <td className="font-mono-ds font-black text-base" data-testid="orders-totals-price">
                  ₹{groupedOrders.reduce((a, g) => a + g.totalAmount, 0).toLocaleString("en-IN")}
                </td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {open && (
        <OrderForm order={editing} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />
      )}
    </div>
  );
}

function OrderGroupRow({ group, onEdit, onDelete, expanded, onToggle }) {
  const meta = STATUS_META[group.status] || {};
  const sizes = [...new Set(group.rows.map((r) => r.size).filter(Boolean))];
  const colors = [...new Set(group.rows.map((r) => r.color).filter(Boolean))];
  const products = [...new Set(group.rows.map((r) => r.product_name).filter(Boolean))];
  const customerName = group.rows.find((r) => r.customer_name)?.customer_name || "";
  const customerPhone = group.rows.find((r) => r.customer_phone)?.customer_phone || "";
  const productSummary = products.length === 0
    ? "—"
    : products.length === 1
      ? products[0]
      : `${products[0]} +${products.length - 1} more`;
  return (
    <>
      <tr className={`${meta.row || ""} cursor-pointer`} data-testid={`order-group-${group.order_no}`} onClick={onToggle}>
        <td className="text-center">
          <button type="button" onClick={(e) => { e.stopPropagation(); onToggle(); }} className="p-1 hover:bg-black/10" aria-label={expanded ? "Collapse" : "Expand"} data-testid={`order-group-toggle-${group.order_no}`}>
            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        </td>
        <td className="font-mono-ds text-xs whitespace-nowrap">{group.order_date}</td>
        <td className="font-mono-ds font-black">{group.order_no}</td>
        <td className="min-w-[180px] max-w-[220px]">
          {customerName || customerPhone ? (
            <div className="leading-tight">
              <div className="font-display font-bold text-sm truncate" title={customerName}>{customerName || "—"}</div>
              {customerPhone && <div className="font-mono-ds text-[10px] text-zinc-600">{customerPhone}</div>}
            </div>
          ) : (
            <span className="font-mono-ds text-[10px] text-zinc-400">—</span>
          )}
        </td>
        <td className="font-display font-bold max-w-[280px] truncate" title={products.join(", ")}>
          {productSummary}
          <span className="ml-2 font-mono-ds text-[10px] text-zinc-500 uppercase">{group.rows.length} line{group.rows.length > 1 ? "s" : ""}</span>
        </td>
        <td className="font-mono-ds font-black text-base">{group.totalQty}<span className="opacity-50 text-xs ml-1">u</span></td>
        <td className="max-w-[130px]">
          <div className="flex flex-wrap gap-1">
            {sizes.map((s) => <span key={s} className="brut-badge text-[10px] py-0.5">{s}</span>)}
          </div>
        </td>
        <td className="text-xs max-w-[130px] truncate" title={colors.join(", ")}>{colors.join(", ") || "—"}</td>
        <td><span className={`brut-badge ${group.payment_mode === "COD" ? "warning" : "success"}`}>{group.payment_mode}</span></td>
        <td><StatusBadge status={group.status} /></td>
        <td className="font-mono-ds font-black">₹{group.totalAmount.toLocaleString("en-IN")}</td>
        <td onClick={(e) => e.stopPropagation()}>
          <div className="flex gap-1">
            <button onClick={() => onEdit(group.rows[0])} className="p-1.5 border-2 border-black hover:bg-zinc-100" title="Edit first line" data-testid={`edit-order-${group.order_no}`}><Edit2 size={12} /></button>
            <button onClick={() => onDelete(group)} className="p-1.5 border-2 border-black hover:bg-[#FF3333] hover:text-white" title="Delete all lines in this order" data-testid={`delete-order-${group.order_no}`}><Trash2 size={12} /></button>
          </div>
        </td>
      </tr>
      {expanded && group.rows.map((r) => (
        <tr key={r.id} className="bg-[#FDF6EE]/60" data-testid={`order-line-${r.id}`}>
          <td></td>
          <td colSpan={2} className="font-mono-ds text-[10px] text-zinc-500 uppercase text-right pr-2">└ line</td>
          <td></td>
          <td className="font-display font-bold text-sm">{r.product_name || "—"} {r.sku && <span className="font-mono-ds text-[10px] text-zinc-500">· {r.sku}</span>}</td>
          <td className="font-mono-ds font-bold">{r.quantity}</td>
          <td><span className="brut-badge text-[10px]">{r.size || "—"}</span></td>
          <td className="text-xs">{r.color || "—"}</td>
          <td></td>
          <td></td>
          <td className="font-mono-ds text-xs">{r.price ? `₹${Number(r.price).toLocaleString("en-IN")} × ${r.quantity}` : "—"}</td>
          <td>
            <div className="flex gap-1">
              <button onClick={() => onEdit(r)} className="p-1.5 border-2 border-black hover:bg-zinc-100" title="Edit this line" data-testid={`edit-order-line-${r.id}`}><Edit2 size={11} /></button>
              <button onClick={() => onDelete(r)} className="p-1.5 border-2 border-black hover:bg-[#FF3333] hover:text-white" title="Delete this line only" data-testid={`delete-order-line-${r.id}`}><Trash2 size={11} /></button>
            </div>
          </td>
        </tr>
      ))}
    </>
  );
}

function Tab({ id, active, setTab, label, count, status }) {
  const isActive = active === id;
  const accent = status ? STATUS_META[status] : null;
  return (
    <button
      onClick={() => setTab(id)}
      data-testid={`orders-tab-${id.toLowerCase()}`}
      className={`px-4 py-2 border-2 border-black font-display font-bold text-xs uppercase tracking-wider transition-all ${
        isActive
          ? (accent ? `${accent.bg} ${accent.text}` : "bg-black text-white") + " shadow-[4px_4px_0_0_#0a0a0a]"
          : "bg-white text-black hover:bg-zinc-100"
      }`}
    >
      {label} <span className="ml-1 font-mono-ds opacity-75">({count})</span>
    </button>
  );
}

function StatusBadge({ status }) {
  const m = STATUS_META[status];
  if (!m) return <span className="brut-badge">{status}</span>;
  return <span className={`brut-badge ${m.bg} ${m.text}`} style={{ borderColor: "#0a0a0a" }}>{m.label}</span>;
}

function Legend({ status }) {
  const m = STATUS_META[status];
  return (
    <div className="inline-flex items-center gap-2 text-xs">
      <span className={`inline-block w-4 h-4 border-2 border-black ${m.row}`} />
      <span className="font-mono-ds text-[10px] tracking-widest uppercase">{m.label}</span>
    </div>
  );
}

function OrderForm({ order, onClose, onSaved }) {
  const isEdit = !!order;
  const emptyBlock = () => ({
    product_name: "", sku: "", color: "", price: 0,
    sizes: [], sizeQty: {},
  });
  const [form, setForm] = useState(() => ({
    order_no: order?.order_no || "",
    order_date: order?.order_date || todayIST(),
    customer_name: order?.customer_name || "",
    customer_phone: order?.customer_phone || "",
    payment_mode: order?.payment_mode || "PAID",
    status: order?.status || "CONFIRMED",
    notes: order?.notes || "",
    // Products array: in edit mode there is exactly one block matching the row.
    products: order
      ? [{
          product_name: order.product_name || "",
          sku: order.sku || "",
          color: order.color || "",
          price: order.price ?? 0,
          sizes: order.size ? [order.size] : [],
          sizeQty: order.size ? { [order.size]: order.quantity ?? 1 } : {},
        }]
      : [emptyBlock()],
  }));
  const [busy, setBusy] = useState(false);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }
  function setBlock(i, patch) {
    setForm((f) => ({ ...f, products: f.products.map((p, idx) => idx === i ? { ...p, ...patch } : p) }));
  }
  function addBlock() { setForm((f) => ({ ...f, products: [...f.products, emptyBlock()] })); }
  function removeBlock(i) {
    setForm((f) => ({ ...f, products: f.products.length > 1 ? f.products.filter((_, idx) => idx !== i) : f.products }));
  }
  function toggleSize(i, sz) {
    setForm((f) => ({
      ...f,
      products: f.products.map((p, idx) => {
        if (idx !== i) return p;
        if (isEdit) return { ...p, sizes: [sz], sizeQty: { [sz]: p.sizeQty[sz] || 1 } };
        const has = p.sizes.includes(sz);
        if (has) {
          const sq = { ...p.sizeQty }; delete sq[sz];
          return { ...p, sizes: p.sizes.filter((s) => s !== sz), sizeQty: sq };
        }
        return { ...p, sizes: [...p.sizes, sz], sizeQty: { ...p.sizeQty, [sz]: 1 } };
      }),
    }));
  }
  function setSizeQty(i, sz, val) {
    const n = Math.max(1, Math.floor(Number(val) || 1));
    setForm((f) => ({
      ...f,
      products: f.products.map((p, idx) => idx === i ? { ...p, sizeQty: { ...p.sizeQty, [sz]: n } } : p),
    }));
  }

  const totalRows = form.products.reduce((a, p) => a + (p.sizes.length || 0), 0);
  const totalUnits = form.products.reduce((a, p) =>
    a + p.sizes.reduce((s, sz) => s + (Number(p.sizeQty[sz]) || 0), 0), 0);
  const totalAmount = form.products.reduce((a, p) => {
    const units = p.sizes.reduce((s, sz) => s + (Number(p.sizeQty[sz]) || 0), 0);
    return a + units * (Number(p.price) || 0);
  }, 0);

  async function save() {
    if (!form.order_no.trim()) { toast.error("Order number is required"); return; }
    const cleanedBlocks = form.products
      .map((p) => ({ ...p, sizes: p.sizes.filter(Boolean) }))
      .filter((p) => p.sizes.length > 0);
    if (cleanedBlocks.length === 0) { toast.error("Pick at least one size on at least one product"); return; }

    // Duplicate order-number warning (only for new orders — edits already point at a row)
    if (!isEdit) {
      try {
        const { data } = await api.get("/orders/check-duplicate", { params: { order_no: form.order_no.trim() } });
        if (data?.exists) {
          const dates = (data.dates || []).slice(0, 3).join(", ");
          const customers = (data.customers || []).slice(0, 2).join(", ");
          const bits = [
            `${data.count} line${data.count > 1 ? "s" : ""}`,
            dates && `on ${dates}`,
            customers && `for ${customers}`,
          ].filter(Boolean).join(" · ");
          const ok = window.confirm(
            `⚠️  Order #${form.order_no.trim()} already exists (${bits}).\n\n` +
            `Creating another one will NOT overwrite the old order — both will co-exist under the same number.\n\n` +
            `Continue anyway?`
          );
          if (!ok) return;
        }
      } catch (_) {
        // If the duplicate check fails, don't block the user — just proceed.
      }
    }

    setBusy(true);
    try {
      if (isEdit) {
        const p = cleanedBlocks[0];
        const sz = p.sizes[0];
        const payload = {
          order_no: form.order_no,
          order_date: form.order_date,
          customer_name: form.customer_name,
          customer_phone: form.customer_phone,
          status: form.status,
          payment_mode: form.payment_mode,
          notes: form.notes,
          product_name: p.product_name,
          sku: p.sku,
          color: p.color,
          price: Number(p.price) || 0,
          size: sz,
          quantity: Number(p.sizeQty[sz]) || 1,
        };
        await api.put(`/orders/${order.id}`, payload);
        toast.success("Order updated");
      } else {
        const line_items = cleanedBlocks.map((p) => ({
          product_name: p.product_name,
          sku: p.sku,
          color: p.color,
          price: Number(p.price) || 0,
          sizes: p.sizes,
          quantities: p.sizes.map((s) => Number(p.sizeQty[s]) || 1),
        }));
        const payload = {
          order_no: form.order_no,
          order_date: form.order_date,
          customer_name: form.customer_name,
          customer_phone: form.customer_phone,
          status: form.status,
          payment_mode: form.payment_mode,
          notes: form.notes,
          line_items,
        };
        const res = await api.post("/orders", payload);
        const count = res.data?.count || 1;
        toast.success(count > 1 ? `Added ${count} order rows` : "Order added");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }

  const meta = STATUS_META[form.status];

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center overflow-y-auto py-8 px-4" data-testid="order-form-modal">
      <div className="brut-card w-full max-w-3xl bg-white">
        <div className={`flex items-center justify-between p-5 border-b-2 border-black ${meta.bg} ${meta.text}`}>
          <div>
            <div className="font-mono-ds text-[10px] tracking-widest uppercase opacity-80">{isEdit ? "EDIT" : "NEW"} · {meta.label}</div>
            <h2 className="font-display font-black text-2xl tracking-tight">{isEdit ? "Edit Order" : "New Order"}</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-black/10" data-testid="close-order-form"><X size={20} /></button>
        </div>

        {/* Shared header fields */}
        <div className="p-6 grid grid-cols-1 md:grid-cols-4 gap-4 border-b-2 border-black">
          <Field label="Order Number *">
            <input className="brut-input font-mono-ds" value={form.order_no} onChange={(e) => set("order_no", e.target.value)} data-testid="order-no-input" placeholder="e.g. CA1043" />
          </Field>
          <Field label="Date">
            <input type="date" className="brut-input font-mono-ds" value={form.order_date} onChange={(e) => set("order_date", e.target.value)} data-testid="order-date-input" />
          </Field>
          <Field label="Payment">
            <select className="brut-input" value={form.payment_mode} onChange={(e) => set("payment_mode", e.target.value)} data-testid="order-payment-select">
              <option value="PAID">PAID</option>
              <option value="COD">COD</option>
            </select>
          </Field>
          <Field label="Status">
            <select className="brut-input" value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="order-status-select">
              <option value="CONFIRMED">Confirmed</option>
              <option value="EXCHANGE">Exchange</option>
              <option value="RETURN">Return</option>
              <option value="CANCELLED">Cancelled by Customer</option>
            </select>
          </Field>
          <div className="md:col-span-2">
            <Field label="Customer Name">
              <input className="brut-input" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} data-testid="order-customer-name-input" placeholder="e.g. Rohan Sharma" />
            </Field>
          </div>
          <div className="md:col-span-2">
            <Field label="Customer Phone">
              <input className="brut-input font-mono-ds" value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} data-testid="order-customer-phone-input" placeholder="e.g. +91 98xxx xxxxx" />
            </Field>
          </div>
        </div>

        {/* Product line-item blocks */}
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="font-mono-ds text-[11px] tracking-widest uppercase text-zinc-600">
              Products · {form.products.length} block{form.products.length > 1 ? "s" : ""}
            </div>
            {!isEdit && (
              <button type="button" onClick={addBlock} className="brut-btn-outline px-3 py-1.5 inline-flex items-center gap-1.5 text-xs" data-testid="add-order-line-btn">
                <Plus size={12} /> Add Product
              </button>
            )}
          </div>

          {form.products.map((p, i) => (
            <div key={i} className="brut-card p-4 bg-[#FDF6EE]/60" data-testid={`order-line-block-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-600">Product #{i + 1}</div>
                {!isEdit && form.products.length > 1 && (
                  <button type="button" onClick={() => removeBlock(i)} className="p-1.5 border-2 border-black hover:bg-[#FF3333] hover:text-white" data-testid={`remove-order-line-${i}`}>
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Product Name">
                  <input className="brut-input" value={p.product_name} onChange={(e) => setBlock(i, { product_name: e.target.value })} data-testid={`order-product-input-${i}`} placeholder="e.g. Pink Floyd Prism Tee" />
                </Field>
                <Field label="SKU">
                  <input className="brut-input font-mono-ds" value={p.sku} onChange={(e) => setBlock(i, { sku: e.target.value })} data-testid={`order-sku-input-${i}`} placeholder="e.g. CA-PF-001" />
                </Field>
                <Field label="Color">
                  <input className="brut-input" value={p.color} onChange={(e) => setBlock(i, { color: e.target.value })} data-testid={`order-color-input-${i}`} placeholder="e.g. Off White" />
                </Field>
                <Field label="Price (₹)">
                  <input type="number" className="brut-input font-mono-ds" value={p.price} onChange={(e) => setBlock(i, { price: e.target.value })} data-testid={`order-price-input-${i}`} />
                </Field>
                <div className="md:col-span-2">
                  <Field label={isEdit ? "Size · Qty" : "Sizes · per-size Qty"}>
                    <div className="flex flex-wrap gap-2" data-testid={`order-sizes-chips-${i}`}>
                      {SIZES.map((s) => {
                        const active = p.sizes.includes(s);
                        return (
                          <div key={s} className={`inline-flex items-stretch border-2 border-black ${active ? "shadow-[3px_3px_0_0_#0a0a0a]" : ""}`}>
                            <button type="button" onClick={() => toggleSize(i, s)} data-testid={`order-size-chip-${i}-${s}`}
                              className={`px-3 py-1.5 font-mono-ds text-xs font-bold tracking-wider transition-all min-w-[44px] ${
                                active ? "bg-black text-white" : "bg-white text-black hover:bg-zinc-100"
                              }`}>{s}</button>
                            {active && (
                              <input type="number" min={1} value={p.sizeQty[s] ?? 1}
                                onChange={(e) => setSizeQty(i, s, e.target.value)}
                                data-testid={`order-size-qty-${i}-${s}`}
                                className="w-14 px-1.5 text-center font-mono-ds font-bold border-l-2 border-black bg-white text-black focus:outline-none" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {p.sizes.length > 0 && (
                      <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-600 mt-1.5">
                        → {p.sizes.length} row{p.sizes.length > 1 ? "s" : ""} · {p.sizes.reduce((a, s) => a + (Number(p.sizeQty[s]) || 0), 0)} units · ₹{(p.sizes.reduce((a, s) => a + (Number(p.sizeQty[s]) || 0), 0) * (Number(p.price) || 0)).toLocaleString("en-IN")}
                      </div>
                    )}
                  </Field>
                </div>
              </div>
            </div>
          ))}

          <Field label="Notes (shared for all products in this order)">
            <textarea rows={2} className="brut-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="order-notes-input" />
          </Field>
        </div>

        <div className="border-t-2 border-black p-5 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
          <div className="bg-black text-white px-4 py-3 flex flex-wrap items-center gap-4 border-2 border-black shadow-[4px_4px_0_0_#0a0a0a]" data-testid="order-form-totals">
            <div>
              <div className="font-mono-ds text-[10px] tracking-widest uppercase opacity-70">Rows · Units</div>
              <div className="font-display font-black text-lg leading-none">{totalRows} · {totalUnits}u</div>
              <div className="font-mono-ds text-[10px] opacity-70 mt-0.5">{form.products.length} product{form.products.length > 1 ? "s" : ""}</div>
            </div>
            <div className="w-px h-8 bg-white/30" />
            <div>
              <div className="font-mono-ds text-[10px] tracking-widest uppercase opacity-70">Total Amount</div>
              <div className="font-display font-black text-lg leading-none" data-testid="order-form-total-amount">₹{totalAmount.toLocaleString("en-IN")}</div>
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <button onClick={onClose} className="brut-btn-outline px-5 py-2.5" data-testid="cancel-order-btn">Cancel</button>
            <button onClick={save} disabled={busy} className="brut-btn px-5 py-2.5 disabled:opacity-60" data-testid="save-order-btn">
              {busy ? "Saving…" : (isEdit ? "Save Changes" : `Save Order${form.products.length > 1 ? ` (${form.products.length} products)` : ""}`)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="font-mono-ds text-[11px] tracking-widest uppercase mb-1.5">{label}</div>
      {children}
    </label>
  );
}
